import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../utils/AuthContext';
import { useUser } from '../utils/UserContext';
import { AcceptPartnerAgentInviteRequest, Entity } from '../types/apiTypes';
import useApi from '../utils/AxiosWrapper';
import { useScreenView } from '../utils/ScreenViewContext';
import { HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import {
  safeSessionGetItem,
  safeSessionSetItem,
  safeSetItem,
} from '../shared/safeStorage';

function PartnerAgentSignup() {
  const { setUserRecord, setUserRole, userRecord, userId } = useUser();
  const { isAuthenticated, user, loginWithRedirect, logout } = useAuth();
  const { isLoading } = useAuth0();
  const { callApi, callProtectedApi } = useApi();
  const [loading, setLoading] = useState(true);
  const { screenView } = useScreenView();
  const [partnerAgentInviteVerified, setPartnerAgentInviteVerified] =
    useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [inviteId, setInviteId] = useState<string>('');
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const tach_token = searchParams.get('tach_token') || '';
  const navigate = useNavigate();

  const verifyPartnerAgentInvite = async () => {
    setLoading(true);
    try {
      const response = await callApi(
        `/partnerAgentInvites/verify`,
        'post',
        { tach_token },
        'Error occurred while verifying partnerAgent invite.'
      );
      const data = response?.data?.data;
      const inviteId = data?.invite_id;
      const email = data?.email;

      if (
        response?.status === HttpStatusCode.Ok &&
        inviteId &&
        inviteId?.length > 0
      ) {
        setPartnerAgentInviteVerified(true);
        setInviteId(inviteId);
        // Default to this route rather than concatenating onto the null a
        // blocked/empty store returns — that produced the literal string
        // "null?partner_agent_invite_id=…", which is truthy enough to pass as a
        // redirectPath while matching no route. `/partner-agent-signup` is the same
        // value the post-verification effect below stores, so the two agree.
        let redirect =
          safeSessionGetItem('redirectPath') ?? '/partner-agent-signup';
        if (!redirect.includes('?partner_agent_invite_id')) {
          redirect = redirect + '?partner_agent_invite_id=' + inviteId;
          safeSessionSetItem('redirectPath', redirect);
        }
        if (email) {
          setInviteEmail(email);
        }
      }
    } catch (e: any) {
      console.error('Error occurred while verifying partnerAgent invite: ', e);
    } finally {
      if (isAuthenticated || !inviteEmail) {
        setLoading(false);
      }
    }
  };

  const acceptPartnerAgentInvite = async (invite_id: string) => {
    try {
      // Critical: Validate that the authenticated user's email matches the invite email
      if (inviteEmail && user?.email) {
        if (inviteEmail.toLowerCase() !== user.email.toLowerCase()) {
          toast.error(
            'Email mismatch detected. Please contact support if you need assistance.'
          );
          setTimeout(() => {
            logout({
              logoutParams: {
                returnTo: window.location.origin + '/login',
              },
            });
          }, 3000);
        }
      }

      const response = await callProtectedApi(
        `/partnerAgentInvites/accept`,
        'post',
        { invite_id, sub_id: user?.sub } as AcceptPartnerAgentInviteRequest,
        'Error occurred while creating your agent account.'
      );
      const partnerAgent = response?.data?.data?.partnerAgent;
      if (
        response?.status === HttpStatusCode.Created &&
        partnerAgent &&
        Object.entries(partnerAgent)?.length > 0
      ) {
        setUserRecord({ ...partnerAgent, userType: Entity.PartnerAgent });
        setUserRole(Entity.PartnerAgent);
        safeSetItem('tachUserRole', Entity.PartnerAgent);
        navigate('/dashboard/referrals');
      } else if (response?.status === HttpStatusCode.InternalServerError) {
        toast.error(
          'Email mismatch detected. Please contact support if you need assistance.'
        );
        setTimeout(() => {
          logout({
            logoutParams: {
              returnTo: window.location.origin + '/login',
            },
          });
        }, 3000);
      }
    } catch (e: any) {
      console.error('Error occurred while verifying agent invite: ', e);
    }
  };

  useEffect(() => {
    // Call verifyPartnerAgentInvite immediately on component mount if tach_token exists
    if (tach_token && !isAuthenticated) {
      verifyPartnerAgentInvite();
    } else {
      // If no tach_token but we have a partner_agent_invite_id in the URL, we're likely returning from Auth0
      if (
        isAuthenticated &&
        userId &&
        userRecord &&
        userRecord?.userType === Entity.PartnerAgent
      ) {
        navigate('/dashboard/referrals');
      }
      const partnerAgentId =
        searchParams.get('partner_agent_invite_id') ||
        safeSessionGetItem('partner_agent_invite_id');
      if (partnerAgentId) {
        setInviteId(partnerAgentId);
        setPartnerAgentInviteVerified(true);
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tach_token, isAuthenticated]); // Only depend on tach_token

  useEffect(() => {
    // Handle Auth0 authentication and partner agent invite acceptance
    if (isAuthenticated && !isLoading && partnerAgentInviteVerified) {
      const partnerAgentId =
        inviteId ||
        searchParams.get('partner_agent_invite_id') ||
        safeSessionGetItem('partner_agent_invite_id');
      if (partnerAgentId && user?.sub) {
        acceptPartnerAgentInvite(partnerAgentId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isLoading,
    partnerAgentInviteVerified,
    inviteId,
    user?.sub,
  ]);

  useEffect(() => {
    // Redirect to Auth0 signup when we have verified the invite and have an email
    if (
      partnerAgentInviteVerified &&
      inviteEmail &&
      !isAuthenticated &&
      !isLoading
    ) {
      safeSessionSetItem('partner_agent_invite_id', inviteId);
      // Store the current path to return to after authentication
      safeSessionSetItem(
        'redirectPath',
        `/partner-agent-signup?partner_agent_invite_id=${inviteId}`
      );

      loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
          login_hint: inviteEmail,
          prompt: 'login',
          scope: 'openid profile email',
        },
        appState: {
          returnTo: `/partner-agent-signup?partner_agent_invite_id=${inviteId}`,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    partnerAgentInviteVerified,
    inviteEmail,
    isAuthenticated,
    isLoading,
    inviteId,
  ]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'mobile':
      case 'tablet':
        return (
          <div>
            {loading ? (
              <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
                <img
                  alt="loading-icon"
                  src="/Icon-v2.gif"
                  className="flex h-44 w-44 justify-center"
                />
                <h2 className="text-[14px] text-secondary">
                  Verifying agent invite link...
                </h2>
              </div>
            ) : (
              <>
                {partnerAgentInviteVerified && (
                  <>
                    {isAuthenticated && !isLoading && (
                      <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
                        <img
                          alt="loading-icon"
                          src="/Icon-v2.gif"
                          className="flex h-44 w-44 justify-center"
                        />
                        <h2 className="text-[14px] text-secondary">
                          Creating your agent account...
                        </h2>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default PartnerAgentSignup;
